export type InteractiveElementScore = { correct: number; total: number };

export enum InteractiveElementStatusKind {
  NotStarted = "not_started",
  InProgress = "in_progress",
  Completed = "completed",
}

export type InteractiveElementStatus =
  | { kind: InteractiveElementStatusKind.NotStarted }
  | {
      kind: InteractiveElementStatusKind.InProgress;
      partial: unknown;
      lastTaskIndex?: number;
    }
  | {
      kind: InteractiveElementStatusKind.Completed;
      score?: InteractiveElementScore;
      // Read-only snapshot, shaped by the element type (QuizReview,
      // WorksheetAnswerRecord). Absent for legacy completions.
      review?: unknown;
      attempts: number;
      completedAt: string;
    };

export const NOT_STARTED: InteractiveElementStatus = {
  kind: InteractiveElementStatusKind.NotStarted,
};

/**
 * Statuses live in memory only, per enrollment, and are gone on reload. The
 * server is the source of truth: each element rebuilds its status from
 * `validation_data` when it loads (see the flows), so a stored copy could only
 * outlive the data it was derived from.
 */
type StateMap = Record<string, InteractiveElementStatus>;

const maps = new Map<string, StateMap>();

const cacheKey = (enrollmentId: string | null): string =>
  enrollmentId ?? "__noenrollment__";

const getMap = (enrollmentId: string | null): StateMap => {
  const key = cacheKey(enrollmentId);
  let map = maps.get(key);
  if (!map) {
    map = {};
    maps.set(key, map);
  }
  return map;
};

const subscribers = new Set<() => void>();

export const subscribeInteractiveElementState = (
  cb: () => void
): (() => void) => {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
};

const notify = (): void => {
  subscribers.forEach((cb) => cb());
};

export const getInteractiveElementState = (
  contentElementId: string,
  enrollmentId: string | null
): InteractiveElementStatus => {
  const map = getMap(enrollmentId);
  return map[contentElementId] ?? NOT_STARTED;
};

export const setInteractiveElementState = (
  contentElementId: string,
  enrollmentId: string | null,
  status: InteractiveElementStatus
): void => {
  const map = getMap(enrollmentId);
  if (status.kind === InteractiveElementStatusKind.NotStarted) {
    delete map[contentElementId];
  } else {
    map[contentElementId] = status;
  }
  notify();
};

export const clearInteractiveElementState = (
  contentElementId: string,
  enrollmentId: string | null
): void => {
  setInteractiveElementState(contentElementId, enrollmentId, NOT_STARTED);
};

/** Test helper: forgets every status and subscriber. */
export const resetInteractiveElementStateForTests = (): void => {
  maps.clear();
  subscribers.clear();
};
